import React, { useState, useEffect } from 'react';
import { TextField, MenuItem, FormHelperText, FormControl } from '@mui/material';

interface YearPickerProps {
  value: number | null;
  onChange: (year: number | null) => void;
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  variant?: 'standard' | 'outlined' | 'filled';
  hideLabel?: boolean;
  disableValidation?: boolean;
}

const YearPicker: React.FC<YearPickerProps> = ({
  value,
  onChange,
  label = 'Jahrgang',
  required = false,
  error = false,
  helperText = '',
  fullWidth = true,
  disabled = false,
  variant = 'outlined',
  hideLabel = false,
  disableValidation = false,
}) => {
  const [years, setYears] = useState<number[]>([]);
  const [internalError, setInternalError] = useState<boolean>(false);
  const [internalHelperText, setInternalHelperText] = useState<string>('');

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const minYear = currentYear - 90; // 90 Jahre zurück
    const maxYear = currentYear - 4;  // 4 Jahre zurück
    
    const yearArray = [];
    for (let year = maxYear; year >= minYear; year--) {
      yearArray.push(year);
    }
    
    setYears(yearArray);
    
    // Validate initial value if present and validation is enabled
    if (value !== null && !disableValidation) {
      validateYear(value);
    }
  }, [value, disableValidation, required]);

  const validateYear = (year: number | null): boolean => {
    if (disableValidation) return true;

    if (year === null) {
      if (required) {
        setInternalError(true);
        setInternalHelperText('Jahrgang ist erforderlich');
        return false;
      }
      setInternalError(false);
      setInternalHelperText('');
      return true;
    }

    const currentYear = new Date().getFullYear();
    const minYear = currentYear - 90;
    const maxYear = currentYear - 4;

    if (year < minYear || year > maxYear) {
      setInternalError(true);
      setInternalHelperText(`Jahrgang muss zwischen ${minYear} und ${maxYear} liegen`);
      return false;
    }

    setInternalError(false);
    setInternalHelperText('');
    return true;
  };

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const yearValue = event.target.value === '' ? null : Number(event.target.value);
    const isValid = validateYear(yearValue);
    
    if (isValid || yearValue === null) {
      onChange(yearValue);
    }
  };

  return (
    <FormControl fullWidth={fullWidth} error={error || internalError}>
      <TextField
        select
        label={hideLabel ? undefined : label}
        value={value === null ? '' : value}
        onChange={handleChange}
        error={error || internalError}
        required={required}
        disabled={disabled}
        fullWidth={fullWidth}
        variant={variant}
        InputLabelProps={hideLabel ? { shrink: false } : undefined}
      >
        <MenuItem value="">
          <em>Auswählen</em>
        </MenuItem>
        {years.map((year) => (
          <MenuItem key={year} value={year}>
            {year}
          </MenuItem>
        ))}
      </TextField>
      {(helperText || internalHelperText) && (
        <FormHelperText>{error ? helperText : internalHelperText}</FormHelperText>
      )}
    </FormControl>
  );
};

export default YearPicker;